import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProductMapGrantsDrawerComponent, GrantConfig } from './product-map-grants-drawer.component';

/**
 * Generic product-id → target mapping editor as a reusable
 * expandable inline section. Designed so any commerce integration
 * (WooCommerce today; Stripe / others later) can drop in:
 *
 *   <app-product-map-editor
 *      [(rows)]="rows"
 *      [scannerCatalog]="catalog"
 *      [membersValue]="'__members__'"
 *      [wildcardValue]="'*'"
 *      title="WooCommerce product map"
 *      keyLabel="WC product_id"
 *      keyPlaceholder="e.g. 95"
 *      [(open)]="sectionOpen">
 *   </app-product-map-editor>
 *
 * Renders as a chevron-expandable card inside whatever parent
 * container hosts it. NOT a modal. The host owns persistence.
 */
@Component({
  standalone: true,
  selector: 'app-product-map-editor',
  imports: [CommonModule, FormsModule, ProductMapGrantsDrawerComponent],
  template: `
<div class="border border-gray-200 dark:border-[#434863] rounded">
  <button type="button"
          class="w-full px-3 py-2 flex items-center justify-between text-left bg-gray-50 dark:bg-app-black-100/40 hover:bg-gray-100 dark:hover:bg-app-blue-100/30"
          (click)="toggle()">
    <span class="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
      <i class="fa-solid fa-arrow-right-arrow-left mr-1 text-emerald-500"></i>
      {{ title || 'Product map' }}
      <span class="ml-2 normal-case text-[10px] text-gray-400">
        {{ rows.length || 0 }} mapping(s){{ subtitle ? ' — ' + subtitle : '' }}
      </span>
    </span>
    <i class="fa-solid" [class.fa-chevron-down]="!open" [class.fa-chevron-up]="open"></i>
  </button>

  <div *ngIf="open" class="p-3 space-y-3">
    <div class="flex items-center justify-between">
      <span class="text-[11px] text-gray-500 dark:text-gray-400">
        Unmatched {{ (keyLabel || 'product') }}s fall back to the integration's default.
      </span>
      <div class="flex items-center gap-2">
        <button type="button"
                *ngIf="rows.length > 0"
                class="text-[10px] uppercase tracking-wide border border-sky-500 text-sky-600 dark:text-sky-400 rounded px-2 py-0.5 hover:bg-sky-50 dark:hover:bg-sky-500/10"
                (click)="copyToClipboard()"
                [title]="'Copy the mapping as TSV — paste into Excel, Google Sheets, or Numbers to share with the customer for review'">
          <i class="fa-solid fa-clipboard mr-1"></i>Copy to Excel
          <span *ngIf="copyStatus" class="ml-1 text-emerald-500">{{ copyStatus }}</span>
        </button>
        <button type="button"
                class="text-[10px] uppercase tracking-wide border border-emerald-500 text-emerald-600 dark:text-emerald-400 rounded px-2 py-0.5 hover:bg-emerald-50 dark:hover:bg-emerald-500/10"
                (click)="addRow()">
          <i class="fa-solid fa-plus mr-1"></i>Add mapping
        </button>
      </div>
    </div>

    <table *ngIf="rows.length > 0" class="w-full text-xs">
      <thead>
        <tr class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-[#434863]">
          <th class="text-left py-1 w-32">{{ keyLabel || 'Product id' }}</th>
          <th *ngIf="hasProductNames()" class="text-left py-1 w-64">Product name
            <span class="ml-1 normal-case text-[9px] text-gray-400">from latest cache</span>
          </th>
          <th class="text-left py-1">{{ targetLabel || 'Target' }}</th>
          <th class="w-16"></th>
        </tr>
      </thead>
      <tbody>
        <ng-container *ngFor="let row of rows; let i = index; trackBy: trackByRowIndex">
        <tr class="align-top">
          <td class="py-1 pr-2">
            <input type="text"
                   class="w-full font-mono text-xs border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                   [(ngModel)]="row.wc_product_id"
                   (ngModelChange)="emitChange()"
                   [placeholder]="keyPlaceholder || 'e.g. 95'" />
          </td>
          <td *ngIf="hasProductNames()" class="py-1 pr-2 align-middle">
            <span *ngIf="nameFor(row.wc_product_id) as n; else noName"
                  class="text-xs text-gray-700 dark:text-gray-200"
                  [title]="n">{{ n }}</span>
            <ng-template #noName>
              <span class="text-[11px] italic text-gray-400 dark:text-gray-500">
                {{ row.wc_product_id ? '(not in latest cache)' : '' }}
              </span>
            </ng-template>
          </td>
          <td class="py-1 pr-2">
            <!-- Bundle mode: grants[] populated → chip summary, dropdown hidden. -->
            <div *ngIf="grantsFor(row).length > 0"
                 class="text-xs px-2 py-1 rounded inline-flex items-center gap-2 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/40 text-emerald-800 dark:text-emerald-200">
              <i class="fa-solid fa-layer-group"></i>
              <span class="font-semibold">Bundle · {{ grantsFor(row).length }} scanner{{ grantsFor(row).length === 1 ? '' : 's' }}</span>
              <span class="text-[10px] text-emerald-700/80 dark:text-emerald-300/80">{{ grantsSummary(row) }}</span>
            </div>
            <!-- Single mode: dropdown as before. -->
            <select *ngIf="grantsFor(row).length === 0"
                    class="w-full text-xs border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1"
                    [(ngModel)]="row.target"
                    (ngModelChange)="emitChange()">
              <option value="">— fallback to default —</option>
              <option value="unassigned" [ngStyle]="{color: '#d97706'}">Unassigned — awaiting categorization (falls back to default)</option>
              <option value="ignore" [ngStyle]="{color: '#6b7280'}">Ignore — product unrelated to F2 scanners (no grant)</option>
              <option *ngIf="membersValue" [value]="membersValue">Members page (no specific scanner)</option>
              <option *ngIf="wildcardValue" [value]="wildcardValue">All customer scanners (wildcard)</option>
              <option *ngFor="let s of scannerCatalog" [value]="s.id" [ngStyle]="s.disabled ? {color: '#dc2626'} : null">
                {{ s.name }} ({{ s.id }}){{ s.disabled ? ' — disabled' : '' }}
              </option>
              <option *ngIf="row.target &&
                             row.target !== 'unassigned' &&
                             row.target !== 'ignore' &&
                             row.target !== membersValue &&
                             row.target !== wildcardValue &&
                             !catalogHas(row.target)"
                      [value]="row.target"
                      [ngStyle]="{color: '#dc2626'}">
                {{ row.target }} (not in customer's current scanner list)
              </option>
            </select>
          </td>
          <td class="py-1 text-right whitespace-nowrap">
            <!-- Grants drawer toggle (⋯) — opens IT-F2-136 v2 editor. -->
            <button type="button"
                    class="text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 text-xs px-1"
                    (click)="toggleDrawer(i)"
                    [title]="isDrawerOpen(i) ? 'Close bundle editor' : 'Configure bundle grants (multi-scanner, per-scanner claims + channels)'">
              <i class="fa-solid" [class.fa-ellipsis]="!isDrawerOpen(i)" [class.fa-chevron-up]="isDrawerOpen(i)"></i>
            </button>
            <button type="button"
                    class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-xs px-1 ml-1"
                    (click)="removeRow(i)"
                    title="Remove this row">
              <i class="fa-solid fa-trash"></i>
            </button>
          </td>
        </tr>
        <tr *ngIf="isDrawerOpen(i)">
          <td [attr.colspan]="hasProductNames() ? 4 : 3" class="pb-2">
            <app-product-map-grants-drawer
              [grants]="grantsFor(row)"
              (grantsChange)="onGrantsChange(i, $event)"
              [scannerCatalog]="scannerCatalog"
              [scannerDocs]="scannerDocs"
              [customer]="customer">
            </app-product-map-grants-drawer>
          </td>
        </tr>
        </ng-container>
      </tbody>
    </table>
    <div *ngIf="rows.length === 0" class="text-[11px] text-gray-500 dark:text-gray-400 italic">
      No mappings yet. Without rows, every event falls through to the integration default. Click "Add mapping" to grant different targets per {{ keyLabel || 'product' }}.
    </div>
  </div>
</div>
  `,
})
export class ProductMapEditorComponent {
  // Expanded/collapsed state. Two-way bound so the host can persist
  // it as a user preference if desired.
  @Input() open = false;
  @Output() openChange = new EventEmitter<boolean>();

  // Mutable row buffer — two-way bound (passed by ref from host). Host
  // diffs against its baseline; this component never persists.
  @Input() rows: Array<{ wc_product_id: string; target: string; __meta?: any }> = [];
  @Output() rowsChange = new EventEmitter<Array<{ wc_product_id: string; target: string; __meta?: any }>>();

  @Input() scannerCatalog: Array<{ id: string; name: string; disabled: boolean }> = [];

  // IT-F2-136 v2 (2026-08-03): full scanner docs keyed by id, needed
  // by the grants drawer to render per-scanner claims_schema widgets +
  // channels multi-select. The scannerCatalog above is a projection
  // for the top-level dropdown; scannerDocs preserves everything else.
  @Input() scannerDocs: Record<string, any> = {};

  // Customer doc — passed through to <app-inline-scanner-claims>
  // inside the grants drawer as the fallback claims_schema source.
  @Input() customer: any = null;

  // IT-F2-87 (2026-08-05): dynamic per-product past-buyer counts, fetched
  // on Copy-to-Excel click (not on popup load) so operators see fresh
  // CommerceEvents state. Host supplies an async callback; editor invokes
  // it inline and folds the returned {pid:count} into the TSV.
  @Input() pastBuyerCountsProvider: (() => Promise<Record<string, number>>) | null = null;

  // { [wc_product_id]: product_name } lookup used to render the
  // human-readable "Product name" column next to the numeric id. When
  // empty (no cache yet, or integration doesn't ship a name map) the
  // column hides entirely -- the editor still works as a two-column
  // id → target mapping.
  @Input() productNames: Record<string, string> = {};

  // Sentinel values. Pass empty string to hide the corresponding
  // dropdown option for an integration that doesn't support that
  // semantic (e.g. Stripe might not want Members page).
  @Input() membersValue = '';
  @Input() wildcardValue = '';

  // Display labels.
  @Input() title = '';
  @Input() subtitle = '';
  @Input() keyLabel = '';
  @Input() targetLabel = '';
  @Input() keyPlaceholder = '';

  // IT-F2-136 v2: per-row drawer open state. Indexed by array position
  // since row identity isn't stable (no server-assigned id yet — a
  // freshly added row starts at index N and stays there). Reset on
  // removeRow to avoid the drawer flapping onto the wrong row.
  private _drawerOpen = new Set<number>();

  addRow() {
    this.rows.push({ wc_product_id: '', target: '' } as any);
    this.emitChange();
  }

  removeRow(i: number) {
    this.rows.splice(i, 1);
    // Rebuild the drawer-open set with indices remapped after splice.
    const rebuilt = new Set<number>();
    for (const idx of this._drawerOpen) {
      if (idx < i) rebuilt.add(idx);
      else if (idx > i) rebuilt.add(idx - 1);
      // idx === i drops (the removed row's drawer state)
    }
    this._drawerOpen = rebuilt;
    this.emitChange();
  }

  // ── IT-F2-136 v2 helpers ──────────────────────────────────────────

  trackByRowIndex(index: number): number {
    return index;
  }

  isDrawerOpen(i: number): boolean {
    return this._drawerOpen.has(i);
  }

  toggleDrawer(i: number) {
    if (this._drawerOpen.has(i)) this._drawerOpen.delete(i);
    else this._drawerOpen.add(i);
  }

  /** Live view of a row's grants[] — stored inside row.__meta so the
   *  host's _serializeProductMap round-trips it via the __meta spread
   *  (no host change required for persistence). Empty array when
   *  grants is unset. */
  grantsFor(row: any): GrantConfig[] {
    const meta = row && row.__meta;
    if (!meta || typeof meta !== 'object') return [];
    const g = meta.grants;
    return Array.isArray(g) ? g : [];
  }

  onGrantsChange(i: number, next: GrantConfig[]) {
    const row: any = this.rows[i];
    if (!row) return;
    if (!row.__meta || typeof row.__meta !== 'object') row.__meta = {};
    row.__meta.grants = next;
    // When bundle grants are populated, the top-level `target` becomes
    // semantic sugar — normalize to `unassigned` so the sentinel path
    // still fires if grants are cleared later. Backend prefers grants[]
    // over target when both are present, so this is safe.
    if (next && next.length > 0) row.target = row.target || 'unassigned';
    this.emitChange();
  }

  /** Compact per-row summary chip content shown next to "Bundle · N scanners":
   *  lists 1-2 scanner names inline, truncates to `+N more` beyond that. */
  grantsSummary(row: any): string {
    const g = this.grantsFor(row);
    if (g.length === 0) return '';
    const names = g.map((x) => {
      const sid = x.scanner_id || '';
      const cat = this.scannerCatalog.find((s) => s.id === sid);
      return cat?.name || sid || '?';
    });
    if (names.length <= 2) return names.join(', ');
    return `${names[0]}, ${names[1]} +${names.length - 2} more`;
  }

  emitChange() {
    this.rowsChange.emit(this.rows);
  }

  catalogHas(id: string): boolean {
    return !!this.scannerCatalog.find(s => s.id === id);
  }

  hasProductNames(): boolean {
    return !!this.productNames && Object.keys(this.productNames).length > 0;
  }

  nameFor(pid: string): string {
    if (!pid || !this.productNames) return '';
    return this.productNames[String(pid).trim()] || '';
  }

  toggle() {
    this.open = !this.open;
    this.openChange.emit(this.open);
  }

  // Transient status shown next to the Copy to Excel button after a
  // successful copy — auto-clears after ~2s so the button doesn't
  // stay in a confirmed state permanently. Empty string = no toast.
  copyStatus = '';

  // IT-F2-90 + IT-F2-136 v2 (2026-08-03): build a TSV (Excel's native
  // paste format). Columns: Product ID / Cart Product Name /
  // Description / Scanner Assignment / Grant Count / Grants (Scanners) /
  // Grants (Claims) / Grants (Channels) / First Seen / Last Seen.
  // Includes the __meta stash (name/description/first_seen_at/
  // last_seen_at) on object-shape rows; string-shape rows (operator-
  // added, no metadata yet) get blanks in those cells.
  //
  // IT-F2-136 v2 additions — for bundle SKUs whose product_map entry
  // carries grants[], emit 3 extra columns so operators can review
  // multi-scanner state in Excel without re-opening the drawer per row:
  //  * Grant Count       — number of grant configs (blank if not a bundle)
  //  * Grants (Scanners) — ';'-joined scanner_ids ordered as stored
  //  * Grants (Claims)   — per-grant claim summary
  //                        `sid1:k=v,k=v; sid2:—; ...`
  //  * Grants (Channels) — per-grant channel subs
  //                        `sid1:ch1|ch2; sid2:—; ...`
  //
  // Escapes any embedded tab/newline in name or description so
  // paste-into-Excel doesn't shift columns; ';'/':' inside claim values
  // are also escaped to '\;' / '\:' to disambiguate the inline format.
  async copyToClipboard(): Promise<void> {
    // IT-F2-87 (2026-08-05): fetch per-product past-buyer counts fresh
    // on click. Silent-fail to empty on error — the TSV still copies
    // with the 'Past Buyers' column blank rather than blocking the whole
    // export. Column ordered right after Product ID so ops eye lands on
    // it first when triaging which unassigned products to categorize.
    let pastBuyers: Record<string, number> = {};
    if (this.pastBuyerCountsProvider) {
      try { pastBuyers = await this.pastBuyerCountsProvider(); } catch { pastBuyers = {}; }
    }
    const header = [
      'Product ID', 'Past Buyers', 'Cart Product Name', 'Description', 'Scanner Assignment',
      'Grant Count', 'Grants (Scanners)', 'Grants (Claims)', 'Grants (Channels)',
      'First Seen', 'Last Seen',
    ];
    const esc = (v: any): string => String(v ?? '').replace(/[\t\r\n]+/g, ' ').trim();
    // Inline-format helper: escape ';' + ':' + ',' inside per-grant
    // details so a claim value with a semicolon can't split the field.
    const escInline = (v: any): string => String(v ?? '')
      .replace(/[\t\r\n]+/g, ' ')
      .replace(/[;:,|]/g, (m) => '\\' + m)
      .trim();
    const nameFromCache = (pid: string) => this.productNames?.[pid] || '';
    const lines: string[] = [header.join('\t')];
    for (const r of this.rows as Array<{ wc_product_id: string; target: string; __meta?: any }>) {
      const pid = esc(r.wc_product_id);
      if (!pid) continue;
      const meta = r.__meta && typeof r.__meta === 'object' ? r.__meta : {};
      const name = esc(meta.name || nameFromCache(pid));
      const desc = esc(meta.description);
      // IT-F2-136 v2: grants[] columns.
      const grants = Array.isArray(meta.grants) ? meta.grants : [];
      // Bundle SKUs normalize `target` to 'unassigned' on save (see
      // onGrantsChange in this component) so the sentinel path still
      // fires if grants[] is cleared later. Reporting 'unassigned' in
      // the exported Scanner Assignment column is misleading — emit
      // 'bundle' when grants[] has entries so the Excel row lines up
      // with the SPA chip ("Bundle · N scanners"). Grant Count +
      // Grants (Scanners) columns carry the details.
      const scanner = grants.length > 0 ? 'bundle' : esc(r.target);
      const first = esc(meta.first_seen_at);
      const last = esc(meta.last_seen_at);
      let gCount = '';
      let gScanners = '';
      let gClaims = '';
      let gChannels = '';
      if (grants.length > 0) {
        gCount = String(grants.length);
        const scanners: string[] = [];
        const claimsParts: string[] = [];
        const chansParts: string[] = [];
        for (const g of grants) {
          if (!g) continue;
          const sid = String(g.scanner_id ?? '').trim();
          if (!sid) continue;
          scanners.push(sid);
          // Only emit a per-grant entry when claims are actually set —
          // no `sid:—; sid:—;` placeholder noise for the common case
          // of unset claims. Blank column when nothing to show.
          const claims = g.claims && typeof g.claims === 'object' ? g.claims : {};
          const kv = Object.keys(claims)
            .filter((k) => k !== 'scanner')
            .filter((k) => {
              const v = claims[k];
              return v !== undefined && v !== null && String(v) !== '';
            })
            .sort()
            .map((k) => `${escInline(k)}=${escInline(claims[k])}`);
          if (kv.length) claimsParts.push(`${escInline(sid)}:${kv.join(',')}`);
          // Channels: same rule — only emit when non-empty.
          const chans = Array.isArray(g.channels) ? g.channels : [];
          if (chans.length) chansParts.push(`${escInline(sid)}:${chans.map(escInline).join('|')}`);
        }
        gScanners = scanners.join(';');
        gClaims = claimsParts.join('; ');
        gChannels = chansParts.join('; ');
      }
      const buyerCount = pastBuyers[pid];
      const buyers = typeof buyerCount === 'number' && buyerCount > 0 ? String(buyerCount) : '';
      lines.push([pid, buyers, name, desc, scanner, gCount, gScanners, gClaims, gChannels, first, last].join('\t'));
    }
    const tsv = lines.join('\n');
    let ok = false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(tsv);
        ok = true;
      } else {
        // Fallback for insecure contexts / older browsers
        const ta = document.createElement('textarea');
        ta.value = tsv;
        ta.style.position = 'fixed'; ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch { ok = false; }
    const rowsCount = lines.length - 1;
    this.copyStatus = ok ? `✓ Copied ${rowsCount} row${rowsCount === 1 ? '' : 's'}` : 'Copy failed';
    setTimeout(() => (this.copyStatus = ''), 2500);
  }
}
